import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle, GraduationCap, ArrowLeft, UploadSimple, FileText, Info } from "@phosphor-icons/react";

const ACCEPT = ".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx";

export default function PublicApply() {
  const { schoolId } = useParams();
  const nav = useNavigate();
  const [schools, setSchools] = useState([]);
  const [pickedId, setPickedId] = useState(schoolId || "");
  const [levels, setLevels] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [reqFiles, setReqFiles] = useState({});    // { document_key: File }
  const [submitted, setSubmitted] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);   // { done, total, current }
  const [form, setForm] = useState({
    child_first_name: "", child_last_name: "", child_birth_date: "", child_gender: "M",
    child_nationality: "", grade_level_id: "", grade_level_name: "",
    guardian_first_name: "", guardian_last_name: "", guardian_email: "",
    guardian_phone: "", guardian_relation: "parent", previous_school: "", additional_info: "",
  });

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/public/schools");
        setSchools(r.data);
        if (!pickedId && r.data.length === 1) setPickedId(r.data[0].id);
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!pickedId) { setLevels([]); return; }
    (async () => {
      try {
        const r = await api.get(`/public/schools/${pickedId}/grade-levels`);
        setLevels(r.data.grade_levels || []);
      } catch (_) { setLevels([]); }
    })();
  }, [pickedId]);

  // Refresh required documents when school or level changes
  useEffect(() => {
    if (!pickedId) { setReqs([]); setReqFiles({}); return; }
    (async () => {
      try {
        const params = form.grade_level_id ? `?grade_level_id=${encodeURIComponent(form.grade_level_id)}` : "";
        const r = await api.get(`/public/admission-requirements/${pickedId}${params}`);
        setReqs(r.data || []);
      } catch (_) { setReqs([]); }
    })();
  }, [pickedId, form.grade_level_id]);

  const pickFile = (key, file) => {
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) { toast.error("Fichier trop volumineux (max 20 Mo)"); return; }
    setReqFiles(prev => ({ ...prev, [key]: file }));
  };
  const dropFile = (key) => setReqFiles(prev => {
    const c = { ...prev }; delete c[key]; return c;
  });

  const toBase64 = (file) => new Promise((res, rej) => {
    const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej;
    r.readAsDataURL(file);
  });

  const submit = async () => {
    if (!pickedId) { toast.error("Choisissez un établissement"); return; }
    for (const f of ["child_first_name", "child_last_name", "child_birth_date",
                     "guardian_first_name", "guardian_last_name", "guardian_email", "guardian_phone"]) {
      if (!form[f]) { toast.error("Merci de remplir tous les champs obligatoires"); return; }
    }
    setSubmitting(true);
    try {
      const payload = { ...form, school_id: pickedId };
      if (!payload.grade_level_id) delete payload.grade_level_id;
      const r = await api.post("/public/applications", payload);
      const data = r.data;
      // Upload any attached required documents in one loop
      const entries = Object.entries(reqFiles);
      if (entries.length > 0 && data.tracking_token) {
        setUploadProgress({ done: 0, total: entries.length, current: "" });
        for (let i = 0; i < entries.length; i++) {
          const [key, file] = entries[i];
          const req = reqs.find(r => r.document_key === key);
          setUploadProgress({ done: i, total: entries.length, current: req?.label || key });
          try {
            const b64 = await toBase64(file);
            await api.post(`/public/applications/track/${data.tracking_token}/documents`, {
              key, label: req?.label || key, data_url: b64, filename: file.name,
            });
          } catch (err) {
            toast.error(`Échec upload "${req?.label || key}" — vous pourrez le faire depuis votre portail de suivi.`);
          }
        }
        setUploadProgress(null);
      }
      setSubmitted(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSubmitting(false); }
  };

  const pickedSchool = schools.find(s => s.id === pickedId);

  if (submitted) {
    const portalUrl = `${window.location.origin}/candidature/${submitted.tracking_token}`;
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="max-w-lg p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-secondary/15 text-secondary mx-auto flex items-center justify-center">
            <CheckCircle size={40} weight="fill" />
          </div>
          <h1 className="mt-4 font-heading text-2xl font-bold">Candidature envoyée !</h1>
          <p className="mt-3 text-sm text-muted-foreground">{submitted.message}</p>
          <div className="mt-4 p-3 bg-muted rounded-lg">
            <div className="text-[10px] uppercase text-muted-foreground">Numéro de candidature</div>
            <div className="font-mono text-lg font-bold">{submitted.application_number}</div>
          </div>
          <div className="mt-4 p-3 bg-primary/5 rounded-lg border border-primary/20 text-left">
            <div className="text-xs font-semibold mb-1">🔗 Suivez votre candidature</div>
            <div className="text-[11px] text-muted-foreground break-all mb-2">{portalUrl}</div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="flex-1"
                      onClick={() => { navigator.clipboard.writeText(portalUrl); toast.success("Lien copié"); }}
                      data-testid="btn-copy-portal">Copier le lien</Button>
              <Button size="sm" className="flex-1"
                      onClick={() => nav(`/candidature/${submitted.tracking_token}`)}
                      data-testid="btn-open-portal">Ouvrir</Button>
            </div>
          </div>
          <Button variant="ghost" className="mt-4" onClick={() => nav("/")} data-testid="btn-back-home">
            Retour à l'accueil
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => nav("/")} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="btn-home">
            <ArrowLeft size={16} /> Accueil
          </button>
          <div className="text-xs uppercase tracking-widest text-secondary font-semibold">Candidature en ligne</div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary/10 text-secondary text-xs font-semibold uppercase tracking-wide">
            <GraduationCap size={14} weight="duotone" /> Inscription
          </div>
          <h1 className="mt-4 font-heading text-3xl font-bold tracking-tight">Admission pour un enfant</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Remplissez ce formulaire — la direction de l'établissement vous recontactera après examen du dossier.
          </p>
        </div>

        <Card className="p-6 space-y-5">
          {/* School selector */}
          <div>
            <Label>Établissement <span className="text-accent">*</span></Label>
            <Select value={pickedId} onValueChange={setPickedId}>
              <SelectTrigger data-testid="select-school"><SelectValue placeholder="Choisir un établissement" /></SelectTrigger>
              <SelectContent>{schools.map(s => (
                <SelectItem key={s.id} value={s.id}>{s.name}{s.city ? ` · ${s.city}` : ""}</SelectItem>
              ))}</SelectContent>
            </Select>
          </div>

          <div className="pt-3 border-t border-border">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">Enfant candidat</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Prénom <span className="text-accent">*</span></Label><Input value={form.child_first_name} onChange={e => setForm({ ...form, child_first_name: e.target.value })} data-testid="input-child-first" /></div>
              <div><Label>Nom <span className="text-accent">*</span></Label><Input value={form.child_last_name} onChange={e => setForm({ ...form, child_last_name: e.target.value })} data-testid="input-child-last" /></div>
              <div><Label>Date de naissance <span className="text-accent">*</span></Label><Input type="date" value={form.child_birth_date} onChange={e => setForm({ ...form, child_birth_date: e.target.value })} data-testid="input-child-birth" /></div>
              <div>
                <Label>Genre</Label>
                <Select value={form.child_gender} onValueChange={v => setForm({ ...form, child_gender: v })}>
                  <SelectTrigger data-testid="select-child-gender"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="M">Garçon</SelectItem>
                    <SelectItem value="F">Fille</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Nationalité</Label><Input value={form.child_nationality} onChange={e => setForm({ ...form, child_nationality: e.target.value })} placeholder="Burkinabè, Ivoirien…" /></div>
              <div>
                <Label>Niveau souhaité</Label>
                <Select value={form.grade_level_id || "unknown"} onValueChange={v => {
                  const gl = levels.find(l => l.id === v);
                  setForm({ ...form, grade_level_id: v === "unknown" ? "" : v, grade_level_name: gl?.name || "" });
                }}>
                  <SelectTrigger data-testid="select-grade-level"><SelectValue placeholder="Choisir…" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unknown">Non défini / à voir avec l'école</SelectItem>
                    {levels.map(l => <SelectItem key={l.id} value={l.id}>{l.name}{l.code ? ` (${l.code})` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4"><Label>École précédente</Label><Input value={form.previous_school} onChange={e => setForm({ ...form, previous_school: e.target.value })} placeholder="Nom + année scolaire précédente" /></div>
          </div>

          <div className="pt-3 border-t border-border">
            <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-3">Parent / Tuteur</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div><Label>Prénom <span className="text-accent">*</span></Label><Input value={form.guardian_first_name} onChange={e => setForm({ ...form, guardian_first_name: e.target.value })} data-testid="input-guardian-first" /></div>
              <div><Label>Nom <span className="text-accent">*</span></Label><Input value={form.guardian_last_name} onChange={e => setForm({ ...form, guardian_last_name: e.target.value })} data-testid="input-guardian-last" /></div>
              <div><Label>Email <span className="text-accent">*</span></Label><Input type="email" value={form.guardian_email} onChange={e => setForm({ ...form, guardian_email: e.target.value })} data-testid="input-guardian-email" /></div>
              <div><Label>Téléphone <span className="text-accent">*</span></Label><Input value={form.guardian_phone} onChange={e => setForm({ ...form, guardian_phone: e.target.value })} placeholder="+226 70 00 00 00" data-testid="input-guardian-phone" /></div>
              <div>
                <Label>Lien avec l'enfant</Label>
                <Select value={form.guardian_relation} onValueChange={v => setForm({ ...form, guardian_relation: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="parent">Parent</SelectItem>
                    <SelectItem value="pere">Père</SelectItem>
                    <SelectItem value="mere">Mère</SelectItem>
                    <SelectItem value="tuteur">Tuteur légal</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="pt-3 border-t border-border">
            <Label>Informations complémentaires</Label>
            <Textarea rows={3} value={form.additional_info} onChange={e => setForm({ ...form, additional_info: e.target.value })}
                      placeholder="Situation particulière, besoins spéciaux, questions…" data-testid="input-additional" />
          </div>

          {/* --- Documents à fournir (dynamiques, optionnels à cette étape) --- */}
          {pickedId && reqs.length > 0 && (
            <div className="pt-3 border-t border-border" data-testid="section-required-docs">
              <div className="flex items-center gap-2 mb-1">
                <FileText size={16} weight="duotone" className="text-primary" />
                <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Documents à fournir</div>
              </div>
              <div className="text-xs text-muted-foreground flex items-start gap-1.5 mb-3">
                <Info size={12} className="shrink-0 mt-0.5" />
                <div>Vous pouvez téléverser dès maintenant les pièces disponibles, ou les ajouter plus tard depuis votre portail de suivi (le lien vous sera fourni après l'envoi).</div>
              </div>
              <div className="space-y-2">
                {reqs.map(r => {
                  const file = reqFiles[r.document_key];
                  return (
                    <div key={r.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border bg-muted/20" data-testid={`req-${r.document_key}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium flex items-center gap-2">
                          {r.label}
                          {r.obligatory
                            ? <span className="text-[9px] uppercase bg-accent/10 text-accent px-1.5 py-0.5 rounded">Obligatoire</span>
                            : <span className="text-[9px] uppercase bg-muted px-1.5 py-0.5 rounded">Facultatif</span>}
                        </div>
                        {file
                          ? <div className="text-xs text-secondary mt-0.5 truncate">✓ {file.name} · {(file.size/1024).toFixed(0)} Ko</div>
                          : <div className="text-xs text-muted-foreground mt-0.5">Non joint — vous pourrez le fournir plus tard.</div>}
                      </div>
                      {file ? (
                        <Button size="sm" variant="ghost" onClick={() => dropFile(r.document_key)} data-testid={`btn-clear-${r.document_key}`}>Retirer</Button>
                      ) : (
                        <label className="cursor-pointer">
                          <input type="file" accept={ACCEPT} className="hidden"
                                 onChange={e => { pickFile(r.document_key, e.target.files?.[0]); e.target.value = ""; }}
                                 data-testid={`input-file-${r.document_key}`} />
                          <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs rounded-md border border-border hover:bg-muted transition">
                            <UploadSimple size={13} /> Téléverser
                          </span>
                        </label>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="pt-3 flex flex-col items-end gap-2">
            {uploadProgress && (
              <div className="w-full text-xs text-muted-foreground text-center bg-muted/30 rounded p-2">
                Téléversement {uploadProgress.done + 1}/{uploadProgress.total} — {uploadProgress.current}…
              </div>
            )}
            <Button onClick={submit} size="lg" disabled={submitting}
                    className="bg-accent hover:bg-accent/90 text-accent-foreground rounded-full px-8"
                    data-testid="btn-submit-application">
              {submitting ? "Envoi en cours…" : "Envoyer ma candidature"}
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
